import type { CeoDebtScheduleItem } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { formatDateRu, formatMillions, formatMoney } from '../ceoFormat';
import {
  DueSubtext,
  ExecutedLabel,
  PaymentRefInput,
  PaymentRefNote,
  ScheduleHeader,
  SectionTitle,
  Table,
  TableWrap,
} from './styles';

interface DebtDetailScheduleTableProps {
  schedule: CeoDebtScheduleItem[];
  totalScheduledPrincipal: number;
  totalScheduledInterest: number;
  totalScheduledFees: number;
  can_write: boolean;
  paidRef: string;
  onPaidRefChange: (val: string) => void;
  onMarkPaid: (itemId: string, amount: number) => void;
  markingItemId: string | null;
  isMarking: boolean;
}

export function DebtDetailScheduleTable({
  schedule,
  totalScheduledPrincipal,
  totalScheduledInterest,
  totalScheduledFees,
  can_write,
  paidRef,
  onPaidRefChange,
  onMarkPaid,
  markingItemId,
  isMarking,
}: DebtDetailScheduleTableProps) {
  return (
    <div>
      <ScheduleHeader>
        <SectionTitle>
          Полный график платежей ({schedule.length} траншей: основной долг{' '}
          {formatMillions(totalScheduledPrincipal)}, проценты/лизинг{' '}
          {formatMillions(totalScheduledInterest)}
          {totalScheduledFees > 0
            ? `, комиссии ${formatMillions(totalScheduledFees)}`
            : ''}
          )
        </SectionTitle>
        {can_write && (
          <PaymentRefInput
            type="text"
            placeholder="Номер п/п для отметки об оплате (необязательно)"
            value={paidRef}
            onChange={(e) => onPaidRefChange(e.target.value)}
          />
        )}
      </ScheduleHeader>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>№</th>
              <th>Дата платежа</th>
              <th>Основной долг</th>
              <th>Проценты / Лизинг</th>
              <th>Комиссия</th>
              <th>Итого платёж</th>
              <th>Остаток долга</th>
              <th>Статус</th>
              {can_write && <th>Действие</th>}
            </tr>
          </thead>
          <tbody>
            {schedule.map((item) => {
              const isPaid = item.status === 'paid';
              const isOverdue = item.status === 'overdue';
              return (
                <tr key={item.id}>
                  <td>#{item.installment_no}</td>
                  <td>
                    <strong>{formatDateRu(item.due_date)}</strong>
                    {!isPaid && (
                      <DueSubtext>
                        {item.days_until_due < 0
                          ? `просрочено на ${Math.abs(item.days_until_due)} дн.`
                          : item.days_until_due === 0
                            ? 'сегодня'
                            : `через ${item.days_until_due} дн.`}
                      </DueSubtext>
                    )}
                  </td>
                  <td>{formatMoney(item.principal_amount)}</td>
                  <td>{formatMoney(item.interest_amount)}</td>
                  <td>{item.fee_amount > 0 ? formatMoney(item.fee_amount) : '—'}</td>
                  <td>
                    <strong>{formatMoney(item.total_payment)}</strong>
                  </td>
                  <td>{formatMoney(item.remaining_principal)}</td>
                  <td>
                    {isPaid ? (
                      <Badge $tone="success">
                        Оплачено {item.paid_date ? formatDateRu(item.paid_date) : ''}
                      </Badge>
                    ) : isOverdue ? (
                      <Badge $tone="danger">Просрочено</Badge>
                    ) : (
                      <Badge $tone="neutral">По графику</Badge>
                    )}
                    {item.payment_reference && (
                      <PaymentRefNote>
                        {item.payment_reference}
                      </PaymentRefNote>
                    )}
                  </td>
                  {can_write && (
                    <td>
                      {!isPaid ? (
                        <Button
                          $size="sm"
                          disabled={isMarking}
                          onClick={() => onMarkPaid(item.id, item.total_payment)}
                        >
                          {markingItemId === item.id && isMarking ? 'Сохранение…' : 'Отметить оплату'}
                        </Button>
                      ) : (
                        <ExecutedLabel>Исполнен</ExecutedLabel>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>
    </div>
  );
}
